import { InboxOutlined } from '@ant-design/icons'
import type { UploadProps } from 'antd'
import { Upload as AntdUpload, Flex, message } from 'antd'
import { useTheme } from 'antd-style'
import { ReactNode } from 'react'

import { Text } from 'src/components'
import { useLocales } from 'src/locales'

import { useStyles } from './styles'

const { Dragger } = AntdUpload

type Props = {
  maxSize?: number
  acceptedTypes?: string[]
  title?: string
  description?: string
  showDescription?: boolean
  icon?: ReactNode
} & UploadProps

export const MAX_FILE_UPLOAD_SIZE = 5 * 1024 * 1024 //5 MB

const acceptedTypesProps = [
  'application/pdf', // PDF
  'application/msword', // DOC
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX
  'application/vnd.ms-excel', // XLS
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // XLSX
  'application/rtf' // RTF
]

export const Upload = ({
  maxSize = MAX_FILE_UPLOAD_SIZE,
  acceptedTypes = acceptedTypesProps,
  title,
  description,
  showDescription = true,
  icon,
  ...uploadProps
}: Props) => {
  const { styles } = useStyles()
  const { t } = useLocales()
  const palette = useTheme()

  const props: UploadProps = {
    name: 'file',
    multiple: true,
    beforeUpload(file) {
      if (acceptedTypes && !acceptedTypes.includes(file.type) && file.type) {
        message.error(t('errors.extension_error'))
        return AntdUpload.LIST_IGNORE
      }
      if (maxSize && file.size > maxSize) {
        message.error(t('errors.size_error'))
        return AntdUpload.LIST_IGNORE
      }
    }
  }

  return (
    <Dragger {...props} {...uploadProps}>
      <p className="ant-upload-drag-icon">
        {icon ?? <InboxOutlined className={styles.icon} />}
      </p>
      <Flex vertical gap={4}>
        <Text size="xl"> {title ?? t('stock_applications.upload_title')}</Text>

        {showDescription && (
          <Text weight={400} color={palette.colorTextDescription}>
            {description ?? t('stock_applications.upload_description')}
          </Text>
        )}
      </Flex>
    </Dragger>
  )
}
